import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Pricing disclaimer above the floor-plans grid. Token path matches Blue Haven's
 * FloorPlansIntro so `custom_pricing_verbiage` and editor chips stay compatible.
 * Heading / body / CTA stay on HeroSolidBG — this section only surfaces pricing copy.
 */

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

export function FloorPlansIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const pricingVerbiage = useContentToken('floorplansintro.pricing_verbiage');

  if (!hasValue(pricingVerbiage)) return null;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-2 pb-6 md:pb-8"
    >
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="floorplansintro.pricing_verbiage"
          as="p"
          className="fpi-pricing font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)]"
        />
      </div>

      <style>{`
        .fpi-pricing,
        .fpi-pricing * {
          font-family: var(--vibe-font-body);
        }
        .fpi-pricing a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansIntro',
  displayName: 'Floor Plans Intro',
  tokens: {
    'floorplansintro.pricing_verbiage': {
      label: 'Pricing Verbiage',
      type: 'richtext',
      defaultField: 'custom_pricing_verbiage',
    },
  },
};

(FloorPlansIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FloorPlansIntro;
